import { useRef, useState } from 'react';
import { Link as RouterLink, NavLink as RouterNavLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Collapse,
  Container,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';

import navConfig from './nav-config';
import useAuth from '../../auth/useAuth';
import useCart from '../../cart/useCart';
import CartDrawer from '../../sections/client/cart/CartDrawer';
import { ADMIN_ROLES, ROLES } from '../../config';

export default function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  // El panel de administracion (/admin) es solo para administrador/tecnico: los links de
  // "mis..." de abajo son self-service del cliente y les darian 403 si los vieran aqui.
  const isStaff = Boolean(user?.role && ADMIN_ROLES.includes(user.role));
  // El distribuidor solo tiene su lista de precios (solo de consulta, ver ClientLayout): no ve
  // los links de navegacion, el buscador, el carrito ni el menu de cliente.
  const isDistributor = user?.role === ROLES.DISTRIBUIDOR;
  const { count: cartCount, isDrawerOpen: cartOpen, openDrawer: openCart, closeDrawer: closeCart } = useCart();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const searchInputRef = useRef(null);

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/', { replace: true });
  };

  const runSearch = () => {
    const term = searchValue.trim();
    if (!term) return;
    navigate(`/catalogo?search=${encodeURIComponent(term)}`);
  };

  const handleSearchIconClick = () => {
    if (!searchOpen) {
      setSearchOpen(true);
      setTimeout(() => searchInputRef.current?.focus(), 0);
      return;
    }
    if (searchValue.trim()) {
      runSearch();
    } else {
      setSearchOpen(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      runSearch();
    } else if (e.key === 'Escape') {
      setSearchValue('');
      setSearchOpen(false);
    }
  };

  return (
    <>
      <AppBar position="sticky" sx={{ borderBottom: (theme) => `1px dashed ${theme.palette.divider}` }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1 }}>
            {!isDistributor && (
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{ display: { xs: 'inline-flex', md: 'none' }, mr: 1 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            <Typography
              variant="h6"
              component={RouterLink}
              to={isDistributor ? '/distribuidor' : '/'}
              sx={{ color: 'primary.main', textDecoration: 'none', flexGrow: { xs: 1, md: 0 }, mr: 4 }}
            >
              PalaciComputers
            </Typography>

            {!isDistributor && (
              <Stack
                direction="row"
                spacing={3}
                sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1 }}
              >
                {navConfig.map((item) => (
                  <Typography
                    key={item.path}
                    component={RouterNavLink}
                    to={item.path}
                    sx={{
                      color: 'text.primary',
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: 14,
                      '&.active': { color: 'primary.main' },
                    }}
                  >
                    {item.title}
                  </Typography>
                ))}

                {!isAuthenticated && (
                  <Typography
                    component={RouterNavLink}
                    to="/login"
                    sx={{
                      color: 'text.primary',
                      textDecoration: 'none',
                      fontWeight: 600,
                      fontSize: 14,
                      '&.active': { color: 'primary.main' },
                    }}
                  >
                    Distribución
                  </Typography>
                )}
              </Stack>
            )}

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', ml: isDistributor ? 'auto' : 0 }}>
              {!isDistributor && (
                <>
                  <Collapse in={searchOpen} orientation="horizontal" sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <TextField
                      inputRef={searchInputRef}
                      size="small"
                      placeholder="Buscar productos..."
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      sx={{ width: 220 }}
                      slotProps={{
                        input: {
                          endAdornment: searchValue ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                aria-label="Limpiar búsqueda"
                                onClick={() => {
                                  setSearchValue('');
                                  searchInputRef.current?.focus();
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        },
                      }}
                    />
                  </Collapse>
                  <IconButton onClick={handleSearchIconClick} aria-label="Buscar productos">
                    <SearchIcon />
                  </IconButton>
                </>
              )}

              {/* El distribuidor solo consulta su lista de precios: no tiene carrito. */}
              {!isDistributor && (
                <IconButton onClick={openCart} aria-label="Abrir carrito">
                  <Badge badgeContent={cartCount} color="primary">
                    <ShoppingCartOutlinedIcon />
                  </Badge>
                </IconButton>
              )}

              {isAuthenticated ? (
                <>
                  <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.lighter', color: 'primary.dark' }}>
                      {(user?.name || 'U').charAt(0).toUpperCase()}
                    </Avatar>
                  </IconButton>
                  <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                    <Box sx={{ px: 2, py: 1.5 }}>
                      <Typography variant="subtitle2" noWrap>
                        {user?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {user?.email}
                      </Typography>
                    </Box>
                    <Divider />
                    {isStaff ? (
                      <MenuItem component={RouterLink} to="/admin" onClick={() => setAnchorEl(null)}>
                        <DashboardOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} />
                        Ir al panel de administración
                      </MenuItem>
                    ) : isDistributor ? (
                      <MenuItem component={RouterLink} to="/distribuidor" onClick={() => setAnchorEl(null)}>
                        <StorefrontOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} />
                        Lista de precios
                      </MenuItem>
                    ) : (
                      <>
                        <MenuItem component={RouterLink} to="/mi-panel" onClick={() => setAnchorEl(null)}>
                          <DashboardOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} />
                          Mi panel
                        </MenuItem>
                        <MenuItem component={RouterLink} to="/mi-cuenta" onClick={() => setAnchorEl(null)}>
                          Mi cuenta
                        </MenuItem>
                        <MenuItem component={RouterLink} to="/mis-cotizaciones" onClick={() => setAnchorEl(null)}>
                          <HistoryIcon fontSize="small" sx={{ mr: 1.5 }} />
                          Mis cotizaciones
                        </MenuItem>
                        <MenuItem component={RouterLink} to="/mis-ordenes-servicio" onClick={() => setAnchorEl(null)}>
                          <BuildOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} />
                          Mis órdenes de servicio
                        </MenuItem>
                        <MenuItem component={RouterLink} to="/mis-pedidos" onClick={() => setAnchorEl(null)}>
                          Mis pedidos
                        </MenuItem>
                      </>
                    )}
                    <Divider />
                    <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                      <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
                      Cerrar sesión
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Button variant="contained" component={RouterLink} to="/login">
                  Iniciar sesión
                </Button>
              )}
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 260 }} role="presentation" onClick={() => setMobileOpen(false)}>
          <Typography variant="h6" sx={{ color: 'primary.main', px: 2, py: 2 }}>
            PalaciComputers
          </Typography>
          <Divider />
          <List>
            {navConfig.map((item) => (
              <ListItemButton key={item.path} component={RouterLink} to={item.path}>
                <ListItemText primary={item.title} />
              </ListItemButton>
            ))}

            {!isAuthenticated && (
              <ListItemButton component={RouterLink} to="/login">
                <ListItemText primary="Distribución" />
              </ListItemButton>
            )}
          </List>
        </Box>
      </Drawer>

      {!isDistributor && <CartDrawer open={cartOpen} onClose={closeCart} />}
    </>
  );
}
